import React, { useState, useMemo } from 'react';
import { TeamInLeague, Player, GameState } from '../../../domain/entities';
import { getPlayerValue } from '../../../domain/constants';
import { PlayerFace } from '../../shared/PlayerFace';
import { useToast } from '../../../contexts/ToastContext';
import { useGameActions } from '../../../contexts/GameActionsContext';
import { soundService } from '../../../services/SoundService';
import { View } from '../../../App';
import { PotentialBadge } from '../../shared/PotentialBadge';
import { FitnessBadge } from '../../shared/FitnessBadge';
import { SellPlayerModal } from '../../modals/SellPlayerModal';
import { AdjustListingPriceModal } from '../../modals/AdjustListingPriceModal';
import { ReleasePlayerModal } from '../../modals/DismissPlayerModal';
import { useTableFilters } from '../../../hooks/useTableFilters';
import { TableHeader } from '../../shared/TableHeader';
import { AdvancedFilterModal } from '../../shared/AdvancedFilterModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAmbulance } from '@fortawesome/free-solid-svg-icons';

interface SellPlayersProps {
    playerTeam: TeamInLeague;
    gameState: GameState;
    onSellPlayer: (player: Player) => Promise<{ success: boolean, message: string }>;
    onReleasePlayer: (player: Player) => Promise<{ success: boolean, message: string }>;
    pushView: (view: View) => void;
}

export const SellPlayers: React.FC<SellPlayersProps> = ({ playerTeam, gameState, onSellPlayer, onReleasePlayer, pushView }) => {
    const { addToast } = useToast();
    const gameActions = useGameActions();
    const [sellModalPlayer, setSellModalPlayer] = useState<Player | null>(null);
    const [adjustModalPlayer, setAdjustModalPlayer] = useState<Player | null>(null);
    const [releaseModalPlayer, setReleaseModalPlayer] = useState<Player | null>(null);

    // Filter state
    const {
        filters: tableFilters,
        updateFilter,
        clearFilters,
        isFilterModalOpen,
        setIsFilterModalOpen,
        hasActiveFilters
    } = useTableFilters('sell-players', {
        search: '',
        sortBy: 'value_desc',
        positionFilter: 'ALL',
        minSkill: 0,
        maxSkill: 99,
        minAge: 16,
        maxAge: 40,
        minValue: 0,
        maxValue: 50000000,
        favFootFilter: 'ALL',
    });

    // Separate listed and non-listed players
    const listedPlayers = useMemo(() => {
        return playerTeam.squad.filter(p => p.isListed);
    }, [playerTeam.squad]);

    const nonListedPlayers = useMemo(() => {
        return playerTeam.squad.filter(p => !p.isListed);
    }, [playerTeam.squad]);

    // Filtered and sorted squad (non-listed only)
    const filteredAndSortedSquad = useMemo(() => {
        return [...nonListedPlayers]
            .filter(p => p.name.toLowerCase().includes(tableFilters.search.toLowerCase()))
            .filter(p => tableFilters.positionFilter === 'ALL' || p.position === tableFilters.positionFilter)
            .filter(p => p.skill >= tableFilters.minSkill && p.skill <= tableFilters.maxSkill)
            .filter(p => p.age >= tableFilters.minAge && p.age <= tableFilters.maxAge)
            .filter(p => {
                const pValue = getPlayerValue(p, gameState.playerLeagueName, gameState.gameType, gameState.config);
                return pValue >= tableFilters.minValue && pValue <= tableFilters.maxValue;
            })
            .filter(p => tableFilters.favFootFilter === 'ALL' || p.favoredFoot === tableFilters.favFootFilter)
            .sort((a, b) => {
                const aValue = getPlayerValue(a, gameState.playerLeagueName, gameState.gameType, gameState.config);
                const bValue = getPlayerValue(b, gameState.playerLeagueName, gameState.gameType, gameState.config);
                switch (tableFilters.sortBy) {
                    case 'value_asc': return aValue - bValue;
                    case 'name_asc': return a.name.localeCompare(b.name);
                    case 'skill_asc': return a.skill - b.skill;
                    case 'skill_desc': return b.skill - a.skill;
                    case 'age_asc': return a.age - b.age;
                    case 'age_desc': return b.age - a.age;
                    case 'value_desc':
                    default: return bValue - aValue;
                }
            });
    }, [nonListedPlayers, tableFilters, gameState.playerLeagueName, gameState.gameType, gameState.config]);

    const handleSell = async () => {
        if (!sellModalPlayer) return;
        const result = await onSellPlayer(sellModalPlayer);
        if (result.success) {
            soundService.playSuccess();
            addToast(`${result.message} Click to view transfer history.`, 'success', () => {
                pushView({ type: 'transfers', title: 'Transfers', activeTab: 'history' });
            });
        } else {
            soundService.playError();
            addToast(result.message, 'error');
        }
        setSellModalPlayer(null);
    };

    const handleRelease = async () => {
        if (!releaseModalPlayer) return;
        const result = await onReleasePlayer(releaseModalPlayer);
        if (result.success) {
            soundService.playSuccess();
            addToast(result.message, 'success');
        } else {
            soundService.playError();
            addToast(result.message, 'error');
        }
        setReleaseModalPlayer(null);
    };

    const handleViewPlayer = (player: Player) => {
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    const handleAdjustPrice = async (newPrice: number) => {
        const result = await gameActions.adjustListingPrice(adjustModalPlayer!, newPrice);
        if (result.success) {
            soundService.playSuccess();
            addToast(result.message, 'success');
        } else {
            soundService.playError();
            addToast(result.message, 'error');
        }
        setAdjustModalPlayer(null);
    };

    const handleUnlistPlayer = async (player: Player) => {
        const result = await gameActions.unlistPlayer(player);
        if (result.success) {
            soundService.playSuccess();
            addToast(result.message, 'success');
        } else {
            soundService.playError();
            addToast(result.message, 'error');
        }
    };

    const isPlayerInLineup = (player: Player) => Object.values(playerTeam.tactics.lineup).includes(player.id);

    return (
        <>
            {/* Listed Players Section */}
            {listedPlayers.length > 0 && (
                <div className="panel" style={{ marginBottom: '24px' }}>
                    <div className="panel-header">Currently Listed for Sale ({listedPlayers.length})</div>
                    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {listedPlayers.map(player => (
                            <div
                                key={player.id}
                                style={{
                                    backgroundColor: 'var(--secondary-color)',
                                    border: '1px solid var(--highlight-color)',
                                    borderRadius: '6px',
                                    padding: '12px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: '12px'
                                }}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <PlayerFace player={player} teamColor={playerTeam.colors.primary} />
                                        {player.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${player.injury.type} Injury (${player.injury.duration} wks)`} />}
                                        <button className="link-style" onClick={() => handleViewPlayer(player)} style={{ fontWeight: '600' }}>
                                            {player.name}
                                        </button>
                                        <span style={{ fontSize: '0.85em', opacity: 0.7 }}>({player.position})</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', fontSize: '0.85em', flexWrap: 'wrap' }}>
                                        <span>Age: <strong>{player.age}</strong></span>
                                        <span>Skill: <strong>{player.skill}</strong></span>
                                        <span>Asking Price: <strong>£{player.askingPrice?.toLocaleString()}</strong></span>
                                        {player.listedDate && (
                                            <span style={{ opacity: 0.7 }}>Listed Week {player.listedDate.week}, Season {player.listedDate.season}</span>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                    <button
                                        className="button small"
                                        onClick={() => setAdjustModalPlayer(player)}
                                        title="Change asking price"
                                    >
                                        Adjust
                                    </button>
                                    <button
                                        className="button small danger"
                                        onClick={() => handleUnlistPlayer(player)}
                                        title="Remove from sale"
                                    >
                                        Unlist
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="panel">
                <div className="panel-header">Available Players</div>
                <TableHeader
                    searchValue={tableFilters.search}
                    onSearchChange={(value) => updateFilter('search', value)}
                    searchPlaceholder="Search player names..."
                    sortValue={tableFilters.sortBy}
                    onSortChange={(value) => updateFilter('sortBy', value)}
                    sortOptions={[
                        { label: 'Value (High to Low)', value: 'value_desc' },
                        { label: 'Value (Low to High)', value: 'value_asc' },
                        { label: 'Skill (High to Low)', value: 'skill_desc' },
                        { label: 'Skill (Low to High)', value: 'skill_asc' },
                        { label: 'Age (Young to Old)', value: 'age_asc' },
                        { label: 'Age (Old to Young)', value: 'age_desc' },
                        { label: 'Name (A to Z)', value: 'name_asc' },
                    ]}
                    onFilterClick={() => setIsFilterModalOpen(true)}
                    onClearFilters={clearFilters}
                    hasActiveFilters={hasActiveFilters}
                />
                <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Pos</th>
                                <th>Cat</th>
                                <th>Foot</th>
                                <th>Age</th>
                                <th>Skl</th>
                                <th>Pot</th>
                                <th>Fit</th>
                                <th>Value</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAndSortedSquad.length === 0 && hasActiveFilters && (
                                <tr>
                                    <td colSpan={10} style={{ textAlign: 'center', padding: '32px 16px', opacity: 0.8 }}>
                                        <p style={{ fontSize: '1em', marginBottom: '8px' }}>No players match your filters.</p>
                                        <p style={{ fontSize: '0.9em', opacity: 0.7 }}>Try adjusting your search criteria or clearing some filters.</p>
                                    </td>
                                </tr>
                            )}
                            {filteredAndSortedSquad.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <PlayerFace player={p} teamColor={playerTeam.colors.primary} />
                                            {p.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${p.injury.type} Injury (${p.injury.duration} wks)`} />}
                                            <button className="link-style" onClick={() => handleViewPlayer(p)}>{p.name}</button>
                                        </div>
                                    </td>
                                    <td>{p.position}</td>
                                    <td title={p.favoredPositionCategory}>{p.favoredPositionCategory?.substring(0, 3)}</td>
                                    <td title={p.favoredFoot}>{p.favoredFoot?.substring(0, 3)}</td>
                                    <td>{p.age}</td>
                                    <td>{p.skill}</td>
                                    <td><PotentialBadge potential={p.potential} /></td>
                                    <td><FitnessBadge fitness={p.fitness} /></td>
                                    <td>£{getPlayerValue(p, gameState.playerLeagueName, gameState.gameType, gameState.config).toLocaleString()}</td>
                                    <td style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                        <button
                                            className="button small"
                                            onClick={() => setSellModalPlayer(p)}
                                            data-ai-target-sell-player-id={p.id}
                                        >
                                            Sell
                                        </button>
                                        <button className="button small danger" onClick={() => setReleaseModalPlayer(p)}>
                                            Release
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <AdvancedFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                filters={tableFilters}
                onFilterChange={updateFilter}
                onClearFilters={clearFilters}
                filterOptions={[
                    { label: 'Position', type: 'select', key: 'positionFilter', options: [
                        { label: 'All Positions', value: 'ALL' },
                        { label: 'Goalkeeper', value: 'GK' },
                        { label: 'Defender', value: 'DEF' },
                        { label: 'Midfielder', value: 'MID' },
                        { label: 'Forward', value: 'FWD' },
                    ]},
                    { label: 'Preferred Foot', type: 'select', key: 'favFootFilter', options: [
                        { label: 'All Feet', value: 'ALL' },
                        { label: 'Left', value: 'Left' },
                        { label: 'Right', value: 'Right' },
                    ]},
                    { label: 'Min Skill', type: 'range', key: 'minSkill', min: 0, max: 99, step: 1 },
                    { label: 'Max Skill', type: 'range', key: 'maxSkill', min: 0, max: 99, step: 1 },
                    { label: 'Min Age', type: 'range', key: 'minAge', min: 16, max: 40, step: 1 },
                    { label: 'Max Age', type: 'range', key: 'maxAge', min: 16, max: 40, step: 1 },
                    { label: 'Min Value', type: 'range', key: 'minValue', min: 0, max: 50000000, step: 100000 },
                    { label: 'Max Value', type: 'range', key: 'maxValue', min: 0, max: 50000000, step: 100000 },
                ]}
                title="Sell Players Filters"
            />

            <SellPlayerModal
                player={sellModalPlayer}
                onClose={() => setSellModalPlayer(null)}
                onConfirm={handleSell}
                isPlayerInLineup={sellModalPlayer ? isPlayerInLineup(sellModalPlayer) : false}
                gameState={gameState}
                leagueName={gameState.playerLeagueName}
            />

            <AdjustListingPriceModal
                player={adjustModalPlayer}
                onClose={() => setAdjustModalPlayer(null)}
                onConfirm={handleAdjustPrice}
                gameState={gameState}
                leagueName={gameState.playerLeagueName}
            />

            <ReleasePlayerModal
                player={releaseModalPlayer}
                onClose={() => setReleaseModalPlayer(null)}
                onConfirm={handleRelease}
                isPlayerInLineup={releaseModalPlayer ? isPlayerInLineup(releaseModalPlayer) : false}
                weeksPerSeason={gameState.config.WEEKS_PER_SEASON}
                leagueName={gameState.playerLeagueName}
                gameType={gameState.gameType}
                gameConfig={gameState.config}
            />
        </>
    );
};
